import type { CellContext } from '@tanstack/react-table';
import type { DataGridColumnDef, DataGridColumnDefOptions } from '../../data-grid.type';
import { cn } from '@utils';
export interface ScoreCellProps {
	current: number;
	total?: number;
	className?: string;
}

export interface ScoreValue {
	current: number;
	total?: number;
}

/**
 * Score Cell component that displays a score in the format "current/total"
 */
const ScoreCell: React.FC<ScoreCellProps> = ({ current, total, className }) => {
	const isTotalDefined = total !== undefined && total !== null;
	return (
		<div className={cn('flex items-center gap-1', className)}>
			<span className="text-primary">{current}</span>
			{isTotalDefined && (
				<>
					<span className="text-primary">/</span>
					<span className="text-secondary">{total}</span>
				</>
			)}
		</div>
	);
};

/**
 * Creates a cell renderer for score cells that display values in a "X/Y" format
 *
 * @param col - The column definition
 * @param options - Additional options including parse function
 * @returns A cell renderer function that creates ScoreCell components
 *
 * @example
 * // Basic usage with parsing function
 * {
 *   key: 'score',
 *   header: 'Score',
 *   type: 'score',
 *   parse: (value) => ({
 *     current: value.achieved,
 *     total: value.maximum
 *   })
 * }
 */
export function getColumnDefScore<T>(col: DataGridColumnDef<T>, options: DataGridColumnDefOptions) {
	return function ScoreCellColumn(context: CellContext<T, unknown>) {
		const value = context.getValue();
		const row = context.row.original;

		if (value === null || value === undefined) return null;

		let scoreProps: ScoreCellProps = {
			current: 0,
			total: 0,
		};

		if (options?.parse) {
			const parsed = options.parse(value, row, options) as ScoreValue;

			if (typeof parsed === 'object' && 'current' in parsed && 'total' in parsed) {
				scoreProps = {
					current: parsed.current,
					total: parsed.total,
				};
			}
		} else if (typeof value === 'object' && 'current' in value && 'total' in value) {
			// Handle direct value assignment if no parse function is provided
			const typedValue = value as ScoreValue;
			scoreProps = {
				current: typedValue.current,
				total: typedValue.total,
			};
		}

		return <ScoreCell {...scoreProps} />;
	};
}
